import { useState } from 'react';
import { UnstyledButton } from '@cowbell/ui/core';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SmoothCollapse } from '../Motion';
import { splitThoughtSections } from './thought-sections';
import styles from './ThinkingBlock.module.css';

export interface ThoughtSectionsProps {
  readonly text: string;
  /** How many of the oldest parts the live chain has folded to make room.
   *  The last part (the one being written) is never folded this way. */
  readonly autoFolded?: number;
}

/**
 * A deep-thinking note as its parts. Each titled part has a row with a
 * chevron; the untitled first part folds to a one-line preview. Parts are
 * open unless the live chain folded them for room or the reader closed them,
 * and the reader can open any folded part again.
 */
export function ThoughtSections({
  text,
  autoFolded = 0,
}: ThoughtSectionsProps) {
  const sections = splitThoughtSections(text);
  const [chosen, setChosen] = useState<Readonly<Record<number, boolean>>>({});
  const lastIndex = sections.length - 1;

  return (
    <div className={styles.liveThought}>
      {sections.map((section, index) => {
        const folded = index < Math.min(autoFolded, lastIndex);
        const open = chosen[index] ?? !folded;
        const toggle = () => setChosen({ ...chosen, [index]: !open });
        const body = (
          <SmoothCollapse open={open}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {section.body}
            </ReactMarkdown>
          </SmoothCollapse>
        );
        if (section.heading.length === 0) {
          return (
            <div key={`part-${index}`} className={styles.thoughtPart}>
              <SmoothCollapse open={!open}>
                <UnstyledButton
                  className={styles.thoughtPreview}
                  onClick={toggle}
                  aria-expanded={false}
                >
                  <span className={styles.thoughtPreviewText}>
                    {section.body.replace(/\*\*|__|`/g, '')}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.rowChevron}
                  />
                </UnstyledButton>
              </SmoothCollapse>
              {body}
            </div>
          );
        }
        return (
          <div key={`part-${index}`} className={styles.thoughtPart}>
            <UnstyledButton
              className={styles.thoughtTitle}
              onClick={toggle}
              aria-expanded={open}
            >
              <span>{section.heading}</span>
              <FontAwesomeIcon
                icon={faChevronRight}
                className={styles.rowChevron}
                rotation={open ? 90 : undefined}
              />
            </UnstyledButton>
            {body}
          </div>
        );
      })}
    </div>
  );
}
